/* eslint-disable @nx/workspace/a11y/require-focus-lock */
/* eslint-disable @nx/workspace/a11y/forbid-missing-initial-focus */
/* eslint-disable @nx/workspace/a11y/require-dialog-role */
'use client';
import React, { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';

import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { ButtonProps } from '@osmosis/tailwind-ui/components/Button';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import { Checkbox } from '@osmosis/tailwind-ui/components/Checkbox';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';

import { PlaylistProps } from '@osmosis/shared/types/playlist';
import { UserProps } from '@osmosis/shared/types/user';
import { ColorProps } from '@osmosis/shared/styles';

import PopupMenu from '../../../components/PopupMenu';
import PopupMenuRow from '../../../components/PopupMenuRow';
import CreatePlaylistBar from '../../../components/CreatePlaylistBar';
import AddToPlaylistIcon from '../../../components/icons/AddToPlaylistIcon';

import { ModalHookProps } from '../../../hooks/useModal';
import { ExpiredUserModalType } from '../../../constants/ExpiredUserModalConfig';

const ExpiredUserLockoutModal = dynamic(
  () => import('../../../components/learn/ExpiredUserModal'),
  { ssr: false },
);
const LearnLockoutModal = dynamic(
  () => import('../../../components/learn/LearnLockoutModal'),
  { ssr: false },
);

type Props = {
  nonPrimeLockoutModal: ModalHookProps;
  signedOutLockoutModal: ModalHookProps;
  popup: ModalHookProps;
  hasPrime: boolean;
  user: UserProps | null;
  isPortal: boolean;
  playlists: PlaylistProps[] | undefined;
  items: { learnNodeID: number; name?: string }[];
  colors: ColorProps;
  'data-testid': string;
  isIconButton: boolean;
  iconButtonProps?: IconButtonProps;
  buttonProps?: ButtonProps;
  tooltipId?: string;
  onRemove: (playlist: PlaylistProps) => void;
  onAdd: (playlist: PlaylistProps) => void;
  onClick: (e: any) => void;
  liveAnnouncement: string;
  hideOnScroll?: boolean;
};

const CheckBoxLabel = ({ title }: { title: string }) => {
  return (
    <P
      title={title}
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '240px',
      }}
    >
      {title}
    </P>
  );
};

const AddToPlaylistButtonView = (props: Props) => {
  const {
    nonPrimeLockoutModal,
    signedOutLockoutModal,
    popup,
    hasPrime,
    user,
    isPortal,
    playlists,
    items = [],
    colors,
    onRemove,
    onAdd,
    isIconButton,
    iconButtonProps = {},
    buttonProps = {},
    tooltipId,
    onClick,
    liveAnnouncement,
    hideOnScroll = false,
  } = props;

  const intl = useIntl();

  const ariaLabel = intl.formatMessage({
    id: 'common.addToPlaylist',
    defaultMessage: 'Add to playlist',
  });

  const playListItems = useMemo(() => {
    return (
      playlists?.map((playlist) => {
        const isChecked = items.every((item) =>
          playlist.list?.find((i) => i.data.nodeID === item.learnNodeID),
        );

        return {
          id: playlist.url,
          component: (
            // eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers
            <Checkbox
              label={<CheckBoxLabel title={playlist.title} />}
              labelClassName="text-gray-700 font-bold text-sm mb-3 cursor-pointer"
              data-testid={`add-to-playlist-${playlist.title}-checkbox`}
              checked={isChecked}
              readOnly
              onClick={() => (isChecked ? onRemove(playlist) : onAdd(playlist))}
            />
          ),
        };
      }) || []
    );
  }, [colors, items, onAdd, onRemove, playlists]);

  return (
    <>
      {!user && signedOutLockoutModal.isVisible && (
        <LearnLockoutModal
          modalProps={signedOutLockoutModal}
          identifier={'playlist'}
        />
      )}

      {!hasPrime && nonPrimeLockoutModal.isVisible && (
        <ExpiredUserLockoutModal
          isVisible={nonPrimeLockoutModal.isVisible}
          hide={nonPrimeLockoutModal.hide}
          modalType={ExpiredUserModalType.PLAYLIST}
        />
      )}

      <PopupMenu
        isPortal={isPortal}
        heading={intl.formatMessage({
          defaultMessage: 'Save to...',
          id: 'add_to_playlist_button.header',
        })}
        liveAnnouncement={liveAnnouncement}
        hideOnScroll={hideOnScroll}
        items={playListItems}
        forceFixedPosition={false}
        isVisible={popup.isVisible}
        hide={popup.hide}
        keyExtractor={(item) => item.id}
        renderItem={(item) => <PopupMenuRow item={item} />}
        displayCreatePlaylistBar={true}
        createPlaylistBar={
          <CreatePlaylistBar
            toggle={popup.toggle}
            handleAddToPlaylist={onAdd}
          />
        }
      >
        {isIconButton && (
          <IconButton
            icon={
              <AddToPlaylistIcon
                fill={iconButtonProps.color || 'textInvert'}
                width={
                  iconButtonProps.iconSize
                    ? iconButtonProps.iconSize + 'px'
                    : undefined
                }
                height={
                  iconButtonProps.iconSize
                    ? iconButtonProps.iconSize + 'px'
                    : undefined
                }
              />
            }
            onClick={onClick}
            data-testid={props['data-testid']}
            aria-label={ariaLabel}
            data-tooltip-id={tooltipId}
            data-prevent-nprogress={true}
            {...iconButtonProps}
          />
        )}

        {!isIconButton && (
          <Button
            className={'${buttonProps.className} h-full'}
            onClick={onClick}
            data-testid={props['data-testid']}
            aria-label={ariaLabel}
            data-tooltip-id={tooltipId}
            data-prevent-nprogress={true}
            {...buttonProps}
          >
            <div className="mr-0 flex items-center md:mr-2">
              {iconButtonProps?.icon ? (
                iconButtonProps.icon
              ) : (
                <AddToPlaylistIcon fill={buttonProps?.color || 'textInvert'} />
              )}
            </div>
            <Box>
              <FormattedMessage
                defaultMessage="Add to playlist"
                id="playlist.add_to_playlist_button"
              />
            </Box>
          </Button>
        )}

        <Tooltip id={tooltipId} style={{ zIndex: ZIndex.MODAL }}>
          <p className="text-sm text-inherit">
            <FormattedMessage
              id="playlist.add_to_playlist_button"
              defaultMessage="Add to playlist"
            />
          </p>
        </Tooltip>
      </PopupMenu>
    </>
  );
};

export default memo(AddToPlaylistButtonView);
